import { useState, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Upload, Download, Loader2, CheckCircle2, XCircle, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SubBrokerBulkUpload() {
  const { api } = useAuth();
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();

  const downloadTemplate = async () => {
    const token = localStorage.getItem("token");
    const resp = await fetch(`${API_URL}/api/subbroker/template`, { headers: { Authorization: `Bearer ${token}` } });
    if (!resp.ok) { toast.error("Failed to download template"); return; }
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "subbroker_upload_template.xlsx"; a.click(); URL.revokeObjectURL(url);
  };

  const handleUpload = async () => {
    if (!file) { toast.error("Pick a file first"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/subbroker/bulk-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Uploaded · ${data.success_count} success, ${data.failure_count} failures`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const downloadErrors = async () => {
    if (!result?.batch_id) return;
    const token = localStorage.getItem("token");
    const resp = await fetch(`${API_URL}/api/subbroker/upload-history/${result.batch_id}/errors`, { headers: { Authorization: `Bearer ${token}` } });
    if (!resp.ok) { toast.error("Failed to download error file"); return; }
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `subbroker_errors_${result.batch_id.slice(0, 8)}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="subbroker-bulk-upload-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-800">Bulk Upload Sub Brokers</h1>
        <p className="text-sm text-gray-500">Upload an XLSX/CSV with the required columns. Use the template to avoid validation errors.</p>
      </div>

      <Card className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-lg text-gray-800">Step 1 — Download Template</h3>
          <Button variant="outline" onClick={downloadTemplate} data-testid="sbu-download-template">
            <Download className="w-4 h-4 mr-2" /> Download Template
          </Button>
        </div>
        <p className="text-xs text-gray-500">Columns: SubBrokerCode, ARNCode, EUIN, SubBrokerName, Mobile, Email, Password, PAN, GST, Address, City, State, Pincode, RMCode, BranchCode, Status</p>
      </Card>

      <Card className="p-5 space-y-4">
        <h3 className="font-semibold text-lg text-gray-800">Step 2 — Upload File</h3>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
          <FileSpreadsheet className="w-12 h-12 mx-auto text-gray-400 mb-2" />
          <input ref={inputRef} type="file" accept=".xlsx,.csv" onChange={(e) => setFile(e.target.files?.[0])} className="hidden" data-testid="sbu-file-input" />
          <div className="space-y-2">
            <Button variant="outline" onClick={() => inputRef.current?.click()} data-testid="sbu-pick-file">Pick File</Button>
            {file && <p className="text-sm text-gray-700">Selected: <b>{file.name}</b> ({Math.round(file.size / 1024)} KB)</p>}
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={handleUpload} disabled={!file || uploading} style={{ backgroundColor: "#1a3f3a" }} data-testid="sbu-upload-btn">
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Upload
          </Button>
        </div>
      </Card>

      {result && (
        <Card className="p-5 space-y-3" data-testid="sbu-result">
          <h3 className="font-semibold text-lg text-gray-800">Upload Result</h3>
          <div className="grid grid-cols-3 gap-3">
            <div className="border border-gray-200 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500">Total Rows</p>
              <p className="text-2xl font-bold text-gray-800">{result.total}</p>
            </div>
            <div className="border-2 border-emerald-200 rounded-lg p-3 text-center bg-emerald-50">
              <p className="text-xs text-emerald-700"><CheckCircle2 className="w-3 h-3 inline" /> Success</p>
              <p className="text-2xl font-bold text-emerald-700">{result.success_count}</p>
            </div>
            <div className="border-2 border-rose-200 rounded-lg p-3 text-center bg-rose-50">
              <p className="text-xs text-rose-700"><XCircle className="w-3 h-3 inline" /> Failures</p>
              <p className="text-2xl font-bold text-rose-700">{result.failure_count}</p>
            </div>
          </div>
          {result.failure_count > 0 && (
            <>
              <Button variant="outline" onClick={downloadErrors} data-testid="sbu-download-errors"><Download className="w-4 h-4 mr-2" /> Download Error File</Button>
              <div className="text-xs space-y-1 bg-rose-50 border border-rose-200 rounded-md p-3 max-h-64 overflow-y-auto">
                <b>First 25 errors:</b>
                {(result.errors_preview || []).map((er, i) => (
                  <div key={i} className="text-rose-700">Row {er.row}: {er.error}</div>
                ))}
              </div>
            </>
          )}
        </Card>
      )}
    </div>
  );
}
